import { useSiteBatteryAnalysis } from "@inrange/building-manager-api-client";
import {
  AllowStringForNumbers,
  EditMode,
  RecursivePartial,
  RecursivePartialSiteAllowStringValues,
  Site,
  SiteAllowStringValues,
  SiteElectricalSystem,
} from "@inrange/building-manager-api-client/models-site";
import {
  buildSiteCalculationPayload,
  SiteCalculationsContext,
  useContextTS,
} from "@inrange/shared-components";
import { formatBatteryModel } from "@inrange/theme-components/formatting";
import _ from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import Loading from "../../Loading";
import CollapsibleCard from "../CollapsibleCard";
import BatteryResults from "./BatteryResults";
import BatterySettings from "./BatterySettings";

const MemoizedBatteryResults = React.memo(BatteryResults);

const Battery = ({
  site,
  esys,
  matchSuffix,
  setSite,
  currencyCode,
  isOwnerOccupied,
  editMode,
}: {
  site: SiteAllowStringValues;
  esys: AllowStringForNumbers<SiteElectricalSystem>;
  matchSuffix?: string;
  setSite: (site: RecursivePartialSiteAllowStringValues) => void;
  currencyCode: string;
  isOwnerOccupied: boolean;
  editMode: EditMode;
}) => {
  const { errors, siteCalculations } = useContextTS(SiteCalculationsContext);
  const [batteryAnalysisPayload, setBatteryAnalysisPayload] = useState<
    { site: RecursivePartial<Site> } | undefined
  >(undefined);
  const [triggerBatteryRequest, setTriggerBatteryRequest] = useState(false);
  const [siteInLastBatteryRequest, setSiteInLastBatteryRequest] = useState<
    SiteAllowStringValues | undefined
  >(undefined);
  const [isAnalysisStale, setIsAnalysisStale] = useState<boolean>(false);
  const [capacityDebugData, setCapacityDebugData] = useState<any>(undefined);
  const [results, setResults] = useState<any[] | undefined>(undefined);
  const [baseline, setBaseline] = useState<any | undefined>(undefined);

  const testMode = !!(!import.meta.env.PROD && import.meta.env.VITE_TEST_MODE);
  const { batteryQueries } = useSiteBatteryAnalysis({
    onlySyntheticBatteries: testMode,
    payload: batteryAnalysisPayload,
    esysIdx: esys.idx,
  });
  const batteryQueriesRef = useRef(batteryQueries);
  batteryQueriesRef.current = batteryQueries;

  const siteRef = useRef<SiteAllowStringValues | undefined>(undefined);
  siteRef.current = site;

  useEffect(() => {
    if (
      !siteRef.current ||
      !siteRef.current.battery![esys.batteryIdx]?.batterySpec
    ) {
      return;
    }
    // Consume the calculated battery values and update the site state
    let update: any = {};
    let updateCount = 0;
    for (const field of [
      "batteryEnergyFlowPayloadHash",
      "batteryEnergyFlowFileVersionId",
    ]) {
      if (
        siteCalculations.battery?.[esys.batteryIdx]?.[field] &&
        siteRef.current &&
        siteCalculations.battery?.[esys.batteryIdx]?.[field] !==
          siteRef.current.battery![esys.batteryIdx]?.[field]
      ) {
        update = {
          ...update,
          battery: {
            [esys.batteryIdx]: {
              ...update.battery?.[esys.batteryIdx],
              [field]: siteCalculations.battery?.[esys.batteryIdx][field],
            },
          },
        };
        updateCount += 1;
      }
    }
    if (
      // Only update the battery cost from the calculations output
      // when the specs change and hence we have a new inferred price
      updateCount > 0 &&
      siteRef.current &&
      siteRef.current.battery[esys.batteryIdx]?.batteryCostKey &&
      siteCalculations.battery?.[esys.batteryIdx]?.batteryLastPriceInputs !==
        siteRef.current.battery[esys.batteryIdx]?.batteryLastPriceInputs
    ) {
      const batteryCostKey =
        siteRef.current.battery[esys.batteryIdx]?.batteryCostKey;
      update = {
        ...update,
        costInputsBattery: {
          costs: {
            [batteryCostKey]:
              siteCalculations.costInputsBattery!.costs[batteryCostKey],
          },
        },
        battery: {
          [esys.batteryIdx]: {
            ...update.battery?.[esys.batteryIdx],
            batteryLastPriceInputs:
              siteCalculations.battery?.[esys.batteryIdx]
                ?.batteryLastPriceInputs,
          },
        },
      };
      updateCount += 1;
    }
    if (updateCount > 0) {
      setSite(update);
    }
  }, [esys, setSite, siteCalculations]);

  const withoutBatteryAttributes = (site: any) => {
    const {
      // Rest of the battery attributes are re-calculated as part of the analysis and so are irrelevant to the comparison
      battery: _battery,
      costInputsBattery: _costInputsBattery,
      // Also ignore notes field as this doesn't impact the analysis
      notes: _notes,
      ...restSite
    } = site;
    return { ...restSite };
  };

  useEffect(() => {
    if (triggerBatteryRequest) {
      for (const query of batteryQueriesRef.current) {
        query.refetch();
      }
      setTriggerBatteryRequest(false);
    }
  }, [triggerBatteryRequest]);

  const isBatteryAnalysisLoading = batteryQueries.some(
    (query) => query.isFetching
  );
  const isBatteryAnalysisLoaded = batteryQueries.every(
    (query) => query.isSuccess && !query.isFetching
  );
  const isBatteryAnalysisError = batteryQueries.some((query) => query.isError);

  useEffect(() => {
    if (isBatteryAnalysisLoaded) {
      const data = batteryQueriesRef.current.reduce(
        (acc: any, query: any) => {
          if (!query.data.batteryResults) {
            return acc;
          }
          acc["batteryResults"] = acc["batteryResults"].concat(
            query.data.batteryResults
          );
          acc["baseline"] = query.data.baseline;
          acc["capacityMwh"] = query.data.capacityMwh;
          return acc;
        },
        {
          batteryResults: [],
          baseline: undefined,
          capacityMwh: undefined,
        }
      );
      setResults(data["batteryResults"]);
      setBaseline(data["baseline"]);
      setCapacityDebugData(data["capacityMwh"]);
    }
  }, [isBatteryAnalysisLoaded]);

  useEffect(() => {
    // If the site changes, mark results as stale
    if (
      siteInLastBatteryRequest &&
      !_.isEqual(
        withoutBatteryAttributes(site),
        withoutBatteryAttributes(siteInLastBatteryRequest)
      )
    ) {
      setIsAnalysisStale(true);
    }
  }, [site, siteInLastBatteryRequest]);

  const disableButtonDueToErrors = Object.keys(errors).length > 0;

  const onClickAnalyze = async () => {
    setIsAnalysisStale(false);
    setSiteInLastBatteryRequest(site);

    const sitePayload = {
      site: buildSiteCalculationPayload({
        site: site as unknown as Partial<Site>,
        clearNonCalcFields: false,
      }),
    };
    setBatteryAnalysisPayload(sitePayload);
    setTriggerBatteryRequest(true);
  };

  return (
    <CollapsibleCard
      title={`Battery${matchSuffix || ""}`}
      summary={
        site.battery[esys.batteryIdx]?.batterySpec
          ? `${formatBatteryModel(site.battery[esys.batteryIdx]?.batterySpec)}`
          : undefined
      }
      className="mt-2"
      dataTestId="battery-section"
      id="battery-section"
      initialOpen={editMode === "SINGLE"}
    >
      <BatterySettings
        site={site}
        esys={esys}
        setSite={setSite}
        currencyCode={currencyCode}
        editMode={editMode}
      />

      <h6 style={{ fontWeight: "bold" }}>Analysis</h6>

      <Row>
        <Col sm={9}>
          Analysis is constrained based on the value of “Applied grid capacity”
          in the DNO and network section.
        </Col>
        <Col sm={3} className="d-grid">
          <Button
            variant="success"
            onClick={onClickAnalyze}
            disabled={
              !isAnalysisStale &&
              (disableButtonDueToErrors ||
                isBatteryAnalysisLoading ||
                results !== undefined)
            }
            data-testid={"run-battery-analysis"}
          >
            Run analysis
          </Button>
        </Col>
      </Row>

      {isAnalysisStale && (
        <Card.Text className={`text-danger mt-3`}>
          Site has been updated since results were generated. Please run a new
          analysis.
        </Card.Text>
      )}

      <div style={{ marginTop: "20px" }}>
        {isBatteryAnalysisLoading && <LoadingResults />}
        {isBatteryAnalysisError && (
          <div>There was an error running the analysis</div>
        )}
        {!isBatteryAnalysisLoading && results !== undefined && (
          <>
            {results.length > 0 && (
              <div data-testid={"battery-results-table"}>
                <MemoizedBatteryResults
                  site={site}
                  esys={esys}
                  setSite={setSite}
                  baseline={baseline}
                  capacityDebugData={capacityDebugData}
                  results={results}
                  currencyCode={currencyCode}
                  isOwnerOccupied={isOwnerOccupied}
                />
              </div>
            )}
            {results.length == 0 && (
              <div>No results with positive IRR found</div>
            )}
          </>
        )}
      </div>
    </CollapsibleCard>
  );
};

export default Battery;

const LoadingResults = () => {
  const label = (
    <p className="text-center">
      Running analysis...
      <br />
      This can take up to 60 seconds. Don&apos;t refresh the page.
    </p>
  );
  return <Loading label={label} height="490px" />;
};
